import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { track } from "@/lib/api";
import { toast } from "sonner";
import {
  UsersThree,
  HandsPraying,
  Sparkle,
  Lock,
  ArrowRight,
  CheckCircle,
  Funnel,
} from "@phosphor-icons/react";

const TOPICS = [
  { key: null,             label: "All" },
  { key: "healing",        label: "Healing" },
  { key: "direction",      label: "Direction" },
  { key: "anxiety",        label: "Peace & Anxiety" },
  { key: "family",         label: "Family" },
  { key: "grief",          label: "Grief" },
  { key: "new-believers",  label: "New Believers" },
  { key: "men",            label: "Men" },
  { key: "women",          label: "Women" },
];

function CircleCard({ c }) {
  const isFull = c.member_count >= c.capacity;
  return (
    <Link
      to={`/circles/${c.slug}`}
      onClick={() => track("circle_card_click", { slug: c.slug, source: "browse" })}
      className="group block"
      data-testid={`circle-card-${c.slug}`}
    >
      <div className="glass-card-strong p-5 transition-all group-hover:-translate-y-0.5 h-full flex flex-col">
        <div className="flex items-start justify-between mb-2">
          <p className="text-[#C4A265] text-[10px] uppercase tracking-[0.2em] font-body font-medium">
            {c.topic.replace("-", " ")}
          </p>
          {c.is_member && <CheckCircle size={14} weight="fill" className="text-emerald-600" />}
          {!c.is_member && c.visibility === "private" && <Lock size={12} className="text-[#8B7355]" />}
        </div>
        <h3 className="font-heading text-lg text-[#2D1B0E] font-medium leading-snug mb-1.5">
          {c.name}
        </h3>
        <p className="font-body text-xs text-[#5C4033] leading-relaxed mb-4 flex-1">
          {c.description}
        </p>
        <div className="flex items-center justify-between text-[11px] font-body">
          <span className="text-[#8B7355]">
            {c.member_count}/{c.capacity} walking together
          </span>
          {isFull ? (
            <span className="text-[#B8A088] italic">Full</span>
          ) : c.is_member ? (
            <span className="text-emerald-700 font-medium inline-flex items-center gap-1">Joined <ArrowRight size={11} /></span>
          ) : (
            <span className="text-[#C4A265] font-medium inline-flex items-center gap-1">Join <ArrowRight size={11} /></span>
          )}
        </div>
      </div>
    </Link>
  );
}

export default function CommunityPage() {
  const [recommended, setRecommended] = useState([]);
  const [circles, setCircles] = useState([]);
  const [filter, setFilter] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [r, l] = await Promise.all([
        api.get("/circles/recommended"),
        api.get("/circles" + (filter ? `?topic=${filter}` : "")),
      ]);
      setRecommended(r.data.items || []);
      setCircles(l.data.items || []);
    } catch {
      toast.error("Couldn't load circles");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  return (
    <div className="px-5 md:px-8 py-4 max-w-5xl mx-auto" data-testid="community-page">
      {/* Hero */}
      <div className="glass-card-strong p-7 mb-7 relative overflow-hidden text-center" data-testid="community-hero">
        <div className="spiritual-orb-sm w-40 h-40 -top-12 -right-10 animate-orb opacity-50" />
        <div className="relative z-10">
          <p className="text-[#C4A265] text-[10px] uppercase tracking-[0.25em] font-body font-medium mb-3 inline-flex items-center gap-1.5">
            <UsersThree size={11} weight="fill" /> Together
          </p>
          <h1 className="font-heading text-2xl sm:text-3xl text-[#2D1B0E] font-medium mb-2">
            You're not walking alone.
          </h1>
          <p className="font-heading text-sm sm:text-base text-[#8B7355] italic mb-2">
            Real people. Real prayer. Rooted in Scripture—together.
          </p>
          <p className="font-body text-xs text-[#B8A088] max-w-md mx-auto leading-relaxed">
            Small, intentional circles of 3–8 people praying through a season together. Not a feed. Not noise.
          </p>
        </div>
      </div>

      {/* Recommended */}
      {recommended.length > 0 && (
        <section className="mb-8" data-testid="recommended-section">
          <div className="flex items-baseline justify-between mb-3">
            <h2 className="font-heading text-xl text-[#2D1B0E] font-medium inline-flex items-center gap-2">
              <Sparkle size={16} weight="fill" className="text-[#C4A265]" />
              Recommended for you
            </h2>
            <span className="text-[#B8A088] text-[11px] font-body italic">Matched to what you're carrying</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {recommended.map((c) => (
              <div key={c.id} onClick={() => track("circle_recommended_clicked", { slug: c.slug })} data-testid={`rec-${c.slug}`}>
                <CircleCard c={c} />
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Browse all */}
      <section className="mb-6" data-testid="browse-section">
        <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
          <h2 className="font-heading text-xl text-[#2D1B0E] font-medium inline-flex items-center gap-2">
            <UsersThree size={16} weight="duotone" className="text-[#C4A265]" />
            Browse all circles
          </h2>
          <div className="inline-flex items-center gap-1 text-[#8B7355] text-xs font-body">
            <Funnel size={12} />
            <span>Filter:</span>
          </div>
        </div>

        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-3 mb-2" data-testid="topic-filters">
          {TOPICS.map((t) => (
            <button
              key={String(t.key)}
              onClick={() => setFilter(t.key)}
              data-testid={`filter-${t.key || "all"}`}
              className={`px-4 py-1.5 rounded-full text-xs font-body whitespace-nowrap transition-all ${
                filter === t.key
                  ? "bg-[#2D1B0E] text-white"
                  : "glass-card text-[#5C4033] hover:bg-white/60"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="glass-card p-6 text-center text-[#8B7355] text-sm font-body">Loading circles…</div>
        ) : circles.length === 0 ? (
          <div className="glass-card p-6 text-center text-[#8B7355] text-sm font-body">No circles match this filter yet.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {circles.map((c) => <CircleCard key={c.id} c={c} />)}
          </div>
        )}
      </section>

      <p className="text-[#B8A088] text-[11px] font-body italic text-center mt-8">
        Pray together. Stay consistent. Walk this faith with others—not alone.
      </p>
    </div>
  );
}
